<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
      </div>
      <div class="columns">
        <div class="column">
          <ag-grid-vue
            style="width: 100%; height: 700px;"
            class="ag-theme-balham"
            :grid-options="gridOptions"
            :pagination="true"
            :pagination-auto-page-size="true"
            :cache-overflow-size="0"
            :multi-sort-key="'ctrl'"
            :get-child-count="getChildCount"
            :modules="modules"
            :get-context-menu-items="getContextMenuItems"
            @grid-ready="gridReady"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AllModules } from '@ag-grid-enterprise/all-modules'
import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'
import gql from 'graphql-tag'
import { mapState } from 'vuex'

export default {
  name: 'Plants',
  mixins: [sortAndFilterMixins],
  data: function () {
    return {
      plants: [],
      totalCount: 0,
      modules: AllModules,
      gridOptions: {
        rowModelType: 'serverSide',
        serverSideStoreType: 'partial',
        cacheBlockSize: 20,
        paginationPageSize: 20,
        enableRangeSelection: true,
        defaultColDef: {
          floatingFilter: true,
          enableValue: true,
          enableRowGroup: true,
          enablePivot: false,
          resizable: true
        },
        columnDefs: [
          { headerName: this.$t('pages.plants.grid.id'),
            field: 'id',
            filter: 'agTextColumnFilter',
            width: 100,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: this.$t('pages.plants.grid.name'),
            field: 'name',
            filter: 'agTextColumnFilter',
            width: 260,
            sortable: true,
            sort: 'asc',
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: this.$t('pages.plants.grid.country'),
            field: 'country',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: this.$t('pages.plants.grid.city'),
            field: 'city',
            filter: 'agTextColumnFilter',
            width: 200,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: this.$t('pages.plants.grid.address'),
            field: 'address',
            filter: 'agTextColumnFilter',
            width: 200,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          }
        ],
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                // tool panel columns won't move when columns are reordered in the grid
                suppressSyncLayoutWithGrid: true,
                // prevents columns being reordered from the Columns Tool Panel
                suppressColumnMove: true
              }
            }
          ],
          defaultToolPanel: ''
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    })
  },
  methods: {
    gridReady: function (params) {
      const dataSource = {
        getRows: async (params) => {
          const groupSummary = this.getGroups(params.request)

          let columns = groupSummary.filter(e => e.isHere === true).map(e => e.column.toString())
          if (!columns.length) {
            columns = this.gridOptions.columnDefs.map(colDef => colDef.field)
          }
          const okPlants = []
          if (this.user.group.plants) {
            this.user.group.plants.forEach(plant => {
              okPlants.push(plant.id)
            })
          }
          const filters = this.getFilters(params.request)
          if (okPlants.length !== 0) {
            if (filters.id) {
              filters.id.in = okPlants
            } else {
              filters.id = {
                in: okPlants
              }
            }
          }
          const builder = this.$gql.query({
            operation: 'plants',
            fields: ['totalCount', { edges: [{ node: [{ aggregate: [{ count: ['id'] }, ...this.getAggregate(params.request)] }, ...columns] }] }],
            variables: {
              skip: { type: 'Int', value: params.request.startRow },
              limit: { type: 'Int', value: params.request.endRow },
              sort: { type: '[sortInputPlant]', value: this.getSorts(params.request) },
              filter: { type: 'filterInputPlant', value: filters },
              group: { type: 'Boolean', value: !!groupSummary.find(e => e.isHere === true) }
            }
          })

          const result = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: gql`${builder.query}`,
            variables: builder.variables })

          const plantsResult = result.data.plants
          this.totalCount = plantsResult.totalCount
          this.plants = plantsResult.edges.map(e => e.node)

          params.successCallback(this.plants, this.totalCount)
        }
      }
      params.api.setServerSideDatasource(dataSource)
    },
    getContextMenuItems (params) {
      const selectedPlant = params.node.data
      let customMenu = []
      const genericMenu = [
        'copy',
        'copyWithHeaders',
        'separator',
        'export'
      ]
      customMenu.push({
        name: this.$t('sidebar.loads'),
        action: () => {
          this.$router.push({ name: 'plant-loads', params: { plant_id: selectedPlant.id } })
        },
        icon: '<i class="fas fa-fw fa-truck-moving" ></i>'
      })
      // if the plant belongs to a producer, show producer route
      if (this.checkIsProducer(selectedPlant.id)) {
        customMenu.push({
          name: this.$t('sidebar.stock'),
          action: () => {
            this.$router.push({ name: 'producer-stock', params: { plant_id: selectedPlant.id } })
          },
          icon: '<i class="fas fa-fw fa-box" ></i>'
        })
      } else {
        customMenu.push({
          name: this.$t('sidebar.production'),
          action: () => {
            this.$router.push({ name: 'plant-production', params: { plant_id: selectedPlant.id } })
          },
          icon: '<i class="fas fa-fw fa-industry" ></i>'
        },
        {
          name: this.$t('sidebar.stock'),
          action: () => {
            this.$router.push({ name: 'plant-stock', params: { plant_id: selectedPlant.id } })
          },
          icon: '<i class="fas fa-fw fa-box" ></i>'
        },
        {
          name: this.$t('sidebar.scans'),
          action: () => {
            this.$router.push({ name: 'plant-scans', params: { plant_id: selectedPlant.id } })
          },
          icon: '<i class="fas fa-fw fa-barcode" ></i>'
        },
        {
            name: this.$t('sidebar.rules'),
            action: () => {
              this.$router.push({ name: 'plant-rules', params: { plant_id: selectedPlant.id } })
            },
            icon: '<i class="fas fa-fw fa-clipboard-list" ></i>'
          }
        )
        // if (this.$store.state.auth.user.role.name === 'Forecast manager') {
        customMenu.push({
          name: 'Forecast',
          action: () => {
            this.$router.push({ name: 'plant-production-forecast', params: { plant_id: selectedPlant.id } })
          },
          icon: '<i class="fas fa-fw fa-step-forward" ></i>'
        })
        // }
      }
      const result = [...customMenu, ...genericMenu]
      return result
    },
    getChildCount: function (data) {
      if (data) {
        return data.aggregate.count.id
      }
    },
    checkIsProducer (code) {
      const regex = /^P\d+/
      return regex.test(code)
    }
  }
}
</script>
